import React, { useContext, useState } from "react";
import { Button, Grid, Typography, ListItemIcon, Fade, Menu, MenuItem, Divider, Box, Drawer, useMediaQuery } from "@mui/material";
import AddIcon from '@mui/icons-material/Add';
import PersonIcon from '@mui/icons-material/Person';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import DeleteIcon from '@mui/icons-material/Delete';
import RemoveIcon from '@mui/icons-material/Remove';
import './Guests.css';
import RoomsContext from '../contexts/RoomsContext';
import { v4 as uuidv4 } from 'uuid';

export default function Guests() {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  // ⬇️ ahora también traemos signalPaxChange
  const { rooms, setRooms, signalPaxChange } = useContext(RoomsContext);

  const [tempRooms, setTempRooms] = useState([...rooms]);
  const [guestSummary, setGuestSummary] = useState({
    roomsCount: rooms.length,
    guestsCount: rooms.reduce((acc, room) => acc + room.adultsCount + room.childrenCount, 0)
  });
  const [openDrawer, setOpenDrawer] = useState(false);
  const isMobileOrTablet = useMediaQuery((theme) => theme.breakpoints.down('md'));

  const handleClick = (event) => {
    if (isMobileOrTablet) {
      setOpenDrawer(true);
    } else {
      setAnchorEl(event.currentTarget);
      setTempRooms([...rooms]);
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
    setOpenDrawer(false);
  };

  const updateGuestSummary = (updatedRooms) => {
    const roomsCount = updatedRooms.length;
    const guestsCount = updatedRooms.reduce((acc, room) => acc + room.adultsCount + room.childrenCount, 0);
    setGuestSummary({ roomsCount, guestsCount });
  };

  const addRoom = () => {
    //const number = rooms.length + 1;
    const number = tempRooms.length + 1;
    const updatedRooms = [...tempRooms, { adultsCount: 2, childrenCount: 0, isInCart: false, roomNumber: number, preferenceRate: false }];
    setTempRooms(updatedRooms);
    updateGuestSummary(updatedRooms);
  };

  const removeRoom = (index) => {
    const updatedRooms = tempRooms.length > 1 ? tempRooms.filter((_, i) => i !== index) : tempRooms;
    setTempRooms(updatedRooms);
    updateGuestSummary(updatedRooms);
  };

  const decreaseCount = (index, type) => {
    const updatedRooms = tempRooms.map((room, i) => {
      if (i === index) {
        return type === "adults"
          ? { ...room, adultsCount: Math.max(room.adultsCount - 1, 0) }
          : { ...room, childrenCount: Math.max(room.childrenCount - 1, 0) };
      }
      return room;
    });
    setTempRooms(updatedRooms);
    updateGuestSummary(updatedRooms);
  };

  const increaseCount = (index, type) => {
    const updatedRooms = tempRooms.map((room, i) => {
      if (i === index) {
        return type === "adults"
          ? { ...room, adultsCount: room.adultsCount + 1 }
          : { ...room, childrenCount: room.childrenCount + 1 };
      }
      return room;
    });
    setTempRooms(updatedRooms);
    updateGuestSummary(updatedRooms);
  };

  // ⬇️ Al guardar, avisamos qué slots cambiaron
  const handleSave = () => {
    const changedSlots = [];
    tempRooms.forEach((r, idx) => {
      const old = rooms[idx];
      const rn = r?.roomNumber ?? (idx + 1);
      if (!old) {
        changedSlots.push(rn); // nueva hab => tratar como cambio
      } else if (
        (r.adultsCount ?? 0) !== (old.adultsCount ?? 0) ||
        (r.childrenCount ?? 0) !== (old.childrenCount ?? 0)
      ) {
        changedSlots.push(rn);
      }
    });

    setRooms(tempRooms);
    if (changedSlots.length) {
      signalPaxChange(changedSlots); // 🔔 avisa para borrar del carrito y alertar
    }
    updateGuestSummary(tempRooms);
    handleClose();
  };

  const handleReset = () => {
    const defaultRooms = [{ adultsCount: 2, childrenCount: 0, isInCart: false, roomNumber: 1 }];
    setTempRooms(defaultRooms);
    updateGuestSummary(defaultRooms);
  };

  const handleCancel = () => {
    setTempRooms([...rooms]);
    handleClose();
  };

  const drawerContent = (
    <Box sx={{ padding: 2 }}>
      <Grid container spacing={2} justifyContent="center" alignItems="center" sx={{ marginBottom: 2 }}>
        <Grid item xs={4} container justifyContent="center" alignItems="center">
          <Typography variant="body1" fontWeight="bold">Habitación</Typography>
        </Grid>
        <Grid item xs={4} container justifyContent="center" alignItems="center">
          <Typography variant="body1" fontWeight="bold">Adultos</Typography>
        </Grid>
        <Grid item xs={4} container justifyContent="center" alignItems="center">
          <Typography variant="body1" fontWeight="bold">Niños</Typography>
        </Grid>
      </Grid>

      {tempRooms.map((room, index) => (
        <Box key={index}>
          <MenuItem sx={{ '&:hover': { backgroundColor: 'transparent' } }}>
            <Grid container spacing={2} justifyContent="center" alignItems="center">
              <Grid item xs={4} container justifyContent="flex-start" alignItems="center">
                <ListItemIcon onClick={() => removeRoom(index)} sx={{ minWidth: 'auto', marginRight: 1 }}>
                  <DeleteIcon fontSize="small" className={tempRooms.length > 1 ? "icon-enabled" : "icon-disabled"} />
                </ListItemIcon>
                <Typography variant="inherit">{`Hab ${index + 1}`}</Typography>
              </Grid>
              <Grid item xs={4} container justifyContent="center" alignItems="center">
                <ListItemIcon onClick={() => decreaseCount(index, "adults")} sx={{ minWidth: 'auto' }}>
                  <RemoveIcon fontSize="small" className={room.adultsCount > 0 ? "icon-enabled" : "icon-disabled"} />
                </ListItemIcon>
                <Typography variant="inherit" className="count-text">{room.adultsCount}</Typography>
                <ListItemIcon onClick={() => increaseCount(index, "adults")} sx={{ minWidth: 'auto' }}>
                  <AddIcon fontSize="small" className="icon-enabled" />
                </ListItemIcon>
              </Grid>
              <Grid item xs={4} container justifyContent="center" alignItems="center">
                <ListItemIcon onClick={() => decreaseCount(index, "children")} sx={{ minWidth: 'auto' }}>
                  <RemoveIcon fontSize="small" className={room.childrenCount > 0 ? "icon-enabled" : "icon-disabled"} />
                </ListItemIcon>
                <Typography variant="inherit" className="count-text">{room.childrenCount}</Typography>
                <ListItemIcon onClick={() => increaseCount(index, "children")} sx={{ minWidth: 'auto' }}>
                  <AddIcon fontSize="small" className="icon-enabled" />
                </ListItemIcon>
              </Grid>
            </Grid>
          </MenuItem>
          {index < tempRooms.length - 1 && <Divider />}
        </Box>
      ))}
      <Divider sx={{ marginY: 2 }} />
      <Grid container spacing={2} justifyContent="space-between" alignItems="center">
        <Grid item xs={6} container justifyContent="flex-start">
          <Button onClick={addRoom} sx={{ textTransform: 'none' }}><AddIcon /> Agregar {isMobileOrTablet ? 'Hab' : 'Habitación'}</Button>
        </Grid>
        <Grid item xs={6} container justifyContent="flex-end">
          <Button onClick={handleReset} sx={{ textTransform: 'none' }}>Restablecer</Button>
        </Grid>
      </Grid>
      <Divider sx={{ marginY: 2 }} />
      <Grid container spacing={2} justifyContent="flex-end" alignItems="center">
        <Grid item>
          <Button variant="outlined" onClick={handleCancel}>Cancelar</Button>
        </Grid>
        <Grid item>
          <Button variant="contained" onClick={handleSave}>Listo</Button>
        </Grid>
      </Grid>
    </Box>
  );

  return (
    <div className="guests">
      <Button
        id="fade-button"
        size="large"
        fullWidth
        aria-controls={open ? 'fade-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        variant="outlined"
        onClick={handleClick}
      >
        <PersonIcon sx={{ color: "#0000008a" }} />
        {guestSummary.roomsCount} hab(s), {guestSummary.guestsCount} pax
        <ArrowDropDownIcon />
      </Button>

      <Drawer
        anchor="bottom"
        open={openDrawer}
        onClose={handleClose}
        sx={{ '& .MuiDrawer-paper': { height: '100%' } }}
      >
        {drawerContent}
      </Drawer>

      {!isMobileOrTablet && (
        <Menu
          id="fade-menu"
          MenuListProps={{ 'aria-labelledby': 'fade-button' }}
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          TransitionComponent={Fade}
          PaperProps={{ style: { width: '400px', padding: '16px' } }}
        >
          {drawerContent}
        </Menu>
      )}
    </div>
  );
}
